/**
 * IndexAddFormBody — name + columns + unique + per-engine extras orchestrator
 * for `add-index`. The engine extras (Slice C1 filtered / partial WHERE
 * textarea + Slice C2 INCLUDE columns input + Slice C3 expression keys
 * textarea) are rendered by the sibling `IndexAddEngineFields`; the C4 Type
 * dropdown + per-kind sub-fields are rendered by `IndexAddTypeField`. The
 * form here owns state and the commit pipeline.
 *
 * Per-engine gating:
 *   - Filter (WHERE …): enabled on mssql / postgres / sqlite. mysql + oracle
 *     render the textarea disabled with the documented C3 functional-index
 *     workaround tooltip.
 *   - INCLUDE columns: enabled on mssql / postgres. mysql / oracle / sqlite
 *     render the input disabled with the documented "no portable workaround"
 *     tooltip (moving columns into the key list changes index semantics).
 *   - Expression keys: enabled on postgres / mysql / oracle / sqlite. mssql
 *     renders the textarea disabled with the computed-column workaround
 *     tooltip (Slice A3 add-column with `generated.expr` then index by name).
 *   - Type dropdown (Slice C4): per-engine option set comes from
 *     `getIndexTypeOptionsForEngine`. Hidden entirely on sqlite (only the
 *     default b-tree is supported). mssql FULLTEXT exposes Key Index +
 *     Catalog sub-inputs; oracle DOMAIN exposes an `INDEXTYPE IS …` input.
 *
 * Slice C5 will add lifecycle actions to the IndexesTab row menu.
 *
 * The target `table` is read from the parked DDL structure when available
 * (CREATE TABLE / ALTER TABLE / CREATE INDEX). Caller supplies a fallback
 * via `tableOverride` when the structure lookup is ambiguous (e.g.
 * blank-buffer Designer scaffolding).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice C1, § Slice C2, § Slice C3, § Slice C4.
 */
import { useMemo, useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  ambiguousColumnNameOf,
  isAmbiguousColumnNameError,
} from '../../../../sql-resolver/ddl/apply/columnEdit.alterTable';
import type {
  IndexColumn,
  IndexDef,
} from '../../../../sql-resolver/types/ddlEdit';
import {
  commitAddIndex,
  getExpressionIndexUnsupportedTooltip,
  getFilteredIndexUnsupportedTooltip,
  getIncludeColumnsUnsupportedTooltip,
  isExpressionIndexSupported,
  isFilteredIndexSupported,
  isIncludeColumnsSupported,
  parseColumnList,
  validateIndexForEngine,
} from '../../edit/edits/addIndex';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ambiguousColumnMessage } from './ambiguousColumnCopy';
import { IndexAddEngineFields } from './IndexAddEngineFields';
import { IndexAddTypeField } from './IndexAddTypeField';
import {
  buildIndexType,
  type IndexTypeSelectValue,
} from './indexTypeOptions';
import { FormEngineChip } from './FormEngineChip';

/** Splits a multi-line expression block into a list of trimmed expression
 *  texts. Empty lines are dropped. Each kept line ships as a discriminated
 *  `{ kind: 'expression', text }` `IndexColumn` entry — DSE2 emits each text
 *  verbatim (or wraps in extra parens on mysql per its 8.0.13+ syntax). */
function parseExpressionList(input: string): readonly string[] {
  return input
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function bareName(name: string): QualifiedName {
  return {
    name,
    raw: name,
    quoted: false,
    foldedName: name.toLowerCase(),
  };
}

function resolveTargetTable(
  editContext: VqeDdlEditContext,
  override: QualifiedName | null | undefined,
): QualifiedName | null {
  if (override) return override;
  try {
    const structure = editContext.service.extractDdlStructure(
      editContext.documentId,
      editContext.statementId,
    );
    if (structure.kind === 'create-table' || structure.kind === 'alter-table') {
      return structure.table;
    }
    if (structure.kind === 'create-index') return structure.index.table;
    if (structure.kind === 'drop-index') return structure.table ?? null;
  } catch {
    // Fall through to null; the caller handles the missing-table error.
  }
  return null;
}

export interface IndexAddFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
  /** Override for the target table when the parked statement does not name
   *  one (rare — Designer with a blank buffer pre-CREATE TABLE). */
  readonly tableOverride?: QualifiedName | null;
}

export function IndexAddFormBody(props: IndexAddFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel, tableOverride } = props;
  const engine = editContext.service.engineId;
  const showFilter = isFilteredIndexSupported(engine);
  const filterTooltip = showFilter ? '' : getFilteredIndexUnsupportedTooltip(engine);
  const showInclude = isIncludeColumnsSupported(engine);
  const includeTooltip = showInclude ? '' : getIncludeColumnsUnsupportedTooltip(engine);
  const showExpression = isExpressionIndexSupported(engine);
  const expressionTooltip = showExpression
    ? ''
    : getExpressionIndexUnsupportedTooltip(engine);

  const targetTable = useMemo(
    () => resolveTargetTable(editContext, tableOverride),
    [editContext, tableOverride],
  );

  const [name, setName] = useState('');
  const [columnsInput, setColumnsInput] = useState('');
  const [unique, setUnique] = useState(false);
  const [filter, setFilter] = useState('');
  const [include, setInclude] = useState('');
  const [expression, setExpression] = useState('');
  const [typeValue, setTypeValue] = useState<IndexTypeSelectValue>('');
  const [mssqlKeyIndex, setMssqlKeyIndex] = useState('');
  const [mssqlCatalog, setMssqlCatalog] = useState('');
  const [oracleIndextype, setOracleIndextype] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The thrown error's own text, kept when the box shows a rewritten
   *  sentence — a developer reading the popover still gets the precise one. */
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildIndex(): IndexDef | { error: string } {
    if (!targetTable) {
      return { error: 'Could not resolve the target table from the parked statement.' };
    }
    const trimmedName = name.trim();
    if (trimmedName === '') {
      return { error: 'Index name is required.' };
    }
    const cols: (string | IndexColumn)[] = [...parseColumnList(columnsInput)];
    if (showExpression) {
      for (const text of parseExpressionList(expression)) {
        cols.push({ kind: 'expression', text });
      }
    }
    let draft: IndexDef = {
      name: bareName(trimmedName),
      table: targetTable,
      columns: cols,
      unique,
    };
    const trimmedFilter = filter.trim();
    if (trimmedFilter !== '' && showFilter) {
      draft = { ...draft, partialWhereText: trimmedFilter };
    }
    if (showInclude) {
      const includeCols = parseColumnList(include);
      if (includeCols.length > 0) {
        draft = { ...draft, includeColumns: includeCols };
      }
    }
    const indexType = buildIndexType(
      typeValue, mssqlKeyIndex, mssqlCatalog, oracleIndextype,
    );
    if (indexType !== undefined) {
      draft = { ...draft, indexType };
    }
    const err = validateIndexForEngine(engine, draft);
    if (err !== null) return { error: err };
    return draft;
  }

  async function handleSubmit() {
    setError(null);
    setErrorDetail(null);
    const built = buildIndex();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAddIndex(editContext, { index: built });
      onCommitted?.();
    } catch (err) {
      const ambiguous = ambiguousColumnNameOf(err);
      if (isAmbiguousColumnNameError(err) && ambiguous !== null) {
        setError(ambiguousColumnMessage(ambiguous, engine, 'type-delimiters'));
        setErrorDetail(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex min-h-0 flex-col">
      {/* Scrollable body — the ONLY scrolling region, bounded by the popover's
          own max-height. `min-h-0` overrides the flex item's default
          `min-height: auto` (content-based floor) so it can actually shrink
          to fit and hand overflow to `overflow-y-auto` instead of pushing the
          footer below off-screen. `-mx-4 px-4` cancels the popover's own
          horizontal padding so the scrollbar track rides the true right edge
          (matches the setup-dialog scroll-body convention). */}
      <div className="-mx-4 min-h-0 flex-1 overflow-y-auto px-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">Add index</div>
            <FormEngineChip engine={engine} />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={`${testId}-name`} className="text-xs">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${testId}-name`}
              data-testid={`${testId}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="idx_table_col"
              className="h-8 font-mono text-xs"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={`${testId}-columns`} className="text-xs">
              Columns <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${testId}-columns`}
              data-testid={`${testId}-columns`}
              value={columnsInput}
              onChange={(e) => setColumnsInput(e.target.value)}
              placeholder="status, created_at"
              className="h-8 font-mono text-xs"
            />
            <span className="text-[10px] text-muted-foreground">Comma-separated column list.</span>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={unique}
              onCheckedChange={(checked) => setUnique(checked === true)}
              data-testid={`${testId}-unique`}
            />
            <span>UNIQUE</span>
          </label>

          <IndexAddEngineFields
            testId={testId}
            showFilter={showFilter}
            filterTooltip={filterTooltip}
            filter={filter}
            setFilter={setFilter}
            showInclude={showInclude}
            includeTooltip={includeTooltip}
            include={include}
            setInclude={setInclude}
            showExpression={showExpression}
            expressionTooltip={expressionTooltip}
            expression={expression}
            setExpression={setExpression}
          />

          <IndexAddTypeField
            testId={testId}
            engine={engine}
            value={typeValue}
            setValue={setTypeValue}
            mssqlKeyIndex={mssqlKeyIndex}
            setMssqlKeyIndex={setMssqlKeyIndex}
            mssqlCatalog={mssqlCatalog}
            setMssqlCatalog={setMssqlCatalog}
            oracleIndextype={oracleIndextype}
            setOracleIndextype={setOracleIndextype}
          />

          {error ? (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
              data-testid={`${testId}-error`}
              title={errorDetail ?? undefined}
            >
              {error}
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer sits OUTSIDE the scrollable region as a normal flex sibling,
          so it always renders in full below the last field — never sliced by
          or overlapping scrolled content. */}
      <div className="-mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
